import React from 'react';
import { Copy, KeyRound, MessageSquareText } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import type { CredenzialiDipendente } from '../../services/dipendentiService';

/**
 * Credenziali del dipendente mostrate una volta sola, dopo la creazione o il reset della password
 * (spec dipendenti §7, stessa idea della password temporanea admin). Chiusa la finestra il genitore
 * dimentica le credenziali: la password non si può più rivedere, si può solo reimpostare.
 */
const CredenzialiUnaVolta: React.FC<{ credenziali: CredenzialiDipendente; onClose: () => void }> = ({ credenziali, onClose }) => {
  const toast = useToast();

  const copia = async (testo: string, conferma: string) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('appunti non disponibili');
      await navigator.clipboard.writeText(testo);
      toast.success(conferma);
    } catch (e) {
      toast.error('Copia non riuscita', 'Seleziona il testo e copialo a mano');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="credenziali-titolo">
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
      <div data-testid="credenziali-una-volta" className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
            <KeyRound className="w-4 h-4 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="credenziali-titolo" className="text-base font-semibold text-gray-900 dark:text-white">Credenziali del dipendente</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Mostrate solo adesso: chiusa questa finestra la password non sarà più visibile.</p>
          </div>
        </div>

        <dl className="space-y-2 text-sm">
          <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800">
            <dt className="text-xs text-gray-500 dark:text-gray-400">Partita IVA dello studio</dt>
            <dd className="font-medium text-gray-900 dark:text-white">{credenziali.partitaIVA}</dd>
          </div>
          <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800">
            <dt className="text-xs text-gray-500 dark:text-gray-400">Nome e cognome</dt>
            <dd className="font-medium text-gray-900 dark:text-white">{credenziali.nominativo}</dd>
          </div>
          <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800">
            <dt className="text-xs text-gray-500 dark:text-gray-400">Password</dt>
            <dd className="flex items-center gap-2 min-w-0">
              <span className="font-mono font-semibold text-gray-900 dark:text-white break-all">{credenziali.password}</span>
              <button
                type="button"
                onClick={() => copia(credenziali.password, 'Password copiata')}
                aria-label="Copia la password"
                className="p-1 rounded text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
              >
                <Copy className="w-4 h-4" aria-hidden="true" />
              </button>
            </dd>
          </div>
        </dl>

        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Il dipendente entra da «Accesso associati» con la scheda «Sono un dipendente». Puoi mandargli il messaggio già pronto.
        </p>

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => copia(credenziali.messaggio, 'Messaggio copiato')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <MessageSquareText className="w-4 h-4" aria-hidden="true" />
            Copia messaggio
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
};

export default CredenzialiUnaVolta;
